import * as React from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Divider,
  Link,
} from "@mui/material";
import SEO from "../components/common/SEO";
import "./TermsPage.css";

export default function TermsPage() {
  return (
    <>
      <SEO
        title="Terms of Service"
        description="Read the terms of service for Loops & Purls. Understand the rules and guidelines for using our knitting pattern chart maker and gallery."
        keywords="terms of service, user agreement, knitting app terms, legal terms"
      />
      <Box component="main" className="fade-in-up">
      <Container maxWidth="lg" className="terms-container">
        {/* Header */}
        <Box className="center mb-4 mt-4">
          <Typography variant="h2" color="primary">
            Terms of Service
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            className="mt-1"
          >
            Last updated: {new Date().toLocaleDateString()}
          </Typography>
          <Box component="hr" className="hr-accent" aria-hidden="true" />
        </Box>

        {/* Content */}
        <Paper
          variant="woven"
          elevation={1}
          className="paper paper--woven mb-4"
        >
          <Box className="terms-content">
            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Acceptance of Terms
              </Typography>
              <Typography variant="body1" className="mb-2">
                By accessing and using Loops & Purls, you accept and agree to be
                bound by the terms and provision of this agreement. If you do not
                agree to abide by the above, please do not use this service.
              </Typography>
              <Typography variant="body1" className="mb-2">
                These Terms of Service apply to all users of the site, including
                without limitation users who are browsers, creators, and
                contributors of content.
              </Typography>
              <Typography variant="body1" className="mb-2">
                <strong>Age Requirements:</strong> You must be at least 13 years old
                to use Loops & Purls. If you are under 13, you may only use this
                service with parental consent and supervision. Users between 13 and
                16 (or the age of majority in your jurisdiction) should have
                parental permission to use this service.
              </Typography>
              <Typography variant="body1">
                <strong>Changes to Terms:</strong> We may update these Terms of
                Service from time to time. Your continued use of the service after
                any changes constitutes your acceptance of the updated Terms.
              </Typography>
            </section>

            <Divider className="my-3" />

            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Use of Service
              </Typography>
              <Typography variant="body1" className="mb-2">
                Loops & Purls is provided for personal and educational use. You
                agree to use the service only for lawful purposes and in a way
                that does not infringe the rights of, restrict, or inhibit anyone
                else's use and enjoyment of the service.
              </Typography>
              <Typography variant="body1" className="mb-2">
                You agree not to:
              </Typography>
              <ul className="mb-2">
                <li>
                  <Typography component="span" variant="body1">
                    Use the service to transmit any harmful or malicious code
                  </Typography>
                </li>
                <li>
                  <Typography component="span" variant="body1">
                    Attempt to gain unauthorized access to any part of the service
                  </Typography>
                </li>
                <li>
                  <Typography component="span" variant="body1">
                    Use automated systems to access the service in a way that
                    places undue burden on the service or interferes with other
                    users' access
                  </Typography>
                </li>
              </ul>
            </section>

            <Divider className="my-3" />

            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                User Content
              </Typography>
              <Typography variant="body1" className="mb-2">
                When you create patterns using Loops & Purls, you retain all
                rights to your content. By sharing patterns in the Gallery, you
                grant Loops & Purls a non-exclusive license to display and
                distribute your patterns through the service.
              </Typography>
              <Typography variant="body1" className="mb-2">
                You are responsible for ensuring that any content you create or
                share does not:
              </Typography>
              <ul className="mb-2">
                <li>
                  <Typography component="span" variant="body1">
                    Violate any third-party rights, including copyright or
                    trademark
                  </Typography>
                </li>
                <li>
                  <Typography component="span" variant="body1">
                    Contain offensive, harmful, or illegal material
                  </Typography>
                </li>
                <li>
                  <Typography component="span" variant="body1">
                    Infringe upon the rights of others
                  </Typography>
                </li>
              </ul>
            </section>

            <Divider className="my-3" />

            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Pattern Sharing and Moderation
              </Typography>
              <Typography variant="body1" className="mb-2">
                The Gallery feature allows users to share their patterns with the
                community. When you submit a pattern to the Gallery, you provide
                your pattern name (required) and optionally your name or other
                identifiable information. All submissions are reviewed before being
                made visible in the Gallery.
              </Typography>
              <Typography variant="body1" className="mb-2">
                <strong>Content Policy:</strong> Loops & Purls is committed to
                maintaining a welcoming and inclusive community. We do not tolerate
                content that promotes discrimination, hatred, or harm toward any
                individual or group based on protected characteristics such as race,
                gender, sexual orientation, religion, or disability. Patterns
                containing such content will be immediately removed, and users who
                repeatedly violate this policy may be banned from the Gallery.
              </Typography>
              <Typography variant="body1" className="mb-2">
                <strong>Removal Requests:</strong> If you wish to have your pattern
                removed from the Gallery, you must{" "}
                <Link
                  href="mailto:hadleyswello@gmail.com?subject=Gallery Request: Pattern Removal"
                  color="primary"
                >
                  email us
                </Link>{" "}
                from the email address associated with your pattern in the Gallery.
                This verification ensures that removal requests come from the pattern
                creator. If you email from a different address, you may still request
                removal if you can provide proof of pattern ownership.
              </Typography>
              <Typography variant="body1" className="mb-2">
                <strong>Copyright Infringement:</strong> If you are a copyright holder
                and believe a pattern in the Gallery infringes upon your rights, you
                may request removal by{" "}
                <Link
                  href="mailto:hadleyswello@gmail.com?subject=Copyright Infringement Report"
                  color="primary"
                >
                  emailing us
                </Link>{" "}
                with proof of your copyright ownership.
              </Typography>
              <Typography variant="body1" className="mb-2">
                <strong>Reporting Harmful Content:</strong> Anyone may report a pattern
                for removal if it contains harmful or offensive content. Use the
                "Report" button on any Gallery pattern to submit a report. All
                reports are reviewed, and patterns that violate our content policy
                will be removed.
              </Typography>
              <Typography variant="body1">
                Gallery patterns are managed manually, and visibility can be adjusted
                at any time. We reserve the right to remove any pattern that violates
                these terms or our content policy.
              </Typography>
            </section>

            <Divider className="my-3" />

            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Intellectual Property
              </Typography>
              <Typography variant="body1" className="mb-2">
                The Loops & Purls service, including its design, functionality,
                and underlying code, is the property of the developer. All rights
                are reserved.
              </Typography>
              <Typography variant="body1">
                Patterns created by users remain the intellectual property of
                those users. Loops & Purls does not claim ownership of user-created
                patterns.
              </Typography>
            </section>

            <Divider className="my-3" />

            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Termination
              </Typography>
              <Typography variant="body1" className="mb-2">
                We reserve the right to suspend or terminate your access to the
                Gallery feature, or remove your Gallery submissions, at any time and
                for any reason, including but not limited to violations of these
                Terms of Service or our content policy.
              </Typography>
              <Typography variant="body1">
                You may stop using the service at any time. If you wish to have your
                Gallery submissions removed, please follow the removal request
                process outlined in the "Pattern Sharing and Moderation" section.
              </Typography>
            </section>

            <Divider className="my-3" />

            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Limitation of Liability
              </Typography>
              <Typography variant="body1" className="mb-2">
                Loops & Purls is provided "as is" without warranties of any kind,
                either express or implied. We do not guarantee that:
              </Typography>
              <ul className="mb-2">
                <li>
                  <Typography component="span" variant="body1">
                    The service will be available at all times
                  </Typography>
                </li>
                <li>
                  <Typography component="span" variant="body1">
                    The service will be free from errors or interruptions
                  </Typography>
                </li>
                <li>
                  <Typography component="span" variant="body1">
                    Data stored locally in your browser will be preserved
                    indefinitely
                  </Typography>
                </li>
              </ul>
              <Typography variant="body1">
                To the extent permitted by law, in no event shall Loops & Purls or
                its developer be liable for any indirect, incidental, special, or
                consequential damages arising from your use of the service.
              </Typography>
            </section>

            <Divider className="my-3" />

            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Governing Law and Jurisdiction
              </Typography>
              <Typography variant="body1">
                These Terms of Service are governed by the laws of the United States
                and the state of Florida, without regard to conflict of law
                principles. Any disputes arising from these Terms or your use of the
                service shall be resolved in the state and federal courts located in
                Florida.
              </Typography>
            </section>

            <Divider className="my-3" />

            <section>
              <Typography variant="h5" color="primary" className="mb-2">
                Contact
              </Typography>
              <Typography variant="body1">
                If you have questions about these Terms of Service, please{" "}
                <Link
                  href="mailto:hadleyswello@gmail.com?subject=Terms of Service Inquiry"
                  color="primary"
                >
                  contact us
                </Link>{" "}
                or reach out through the About page.
              </Typography>
            </section>
          </Box>
        </Paper>
      </Container>
    </Box>
    </>
  );
}

